import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getBlogPosts, getSiteSettings } from '../lib/api';
import { useSEO, buildOrganizationSchema, buildWebSiteSchema } from '../lib/seo';
import './HomePage.css';
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ── Fallback products with real photos ── */
const FALLBACK_FEATURED = [
  {
    id:1, name:'Monstera Deliciosa', slug:'monstera-deliciosa', category:'statement',
    price:1299, originalPrice:1599, rating:4.8, reviews:128, badge:'Bestseller',
    description:'The Swiss Cheese Plant — iconic split leaves that bring bold, tropical drama to any room.',
    care:{ light:'Indirect bright light', water:'Weekly', difficulty:'Easy' },
    tags:['tropical'], inStock:true, image:'monstera',
    imageUrl:'https://images.unsplash.com/photo-1614594975525-e45190c55d0b?w=600&h=600&fit=crop&auto=format&q=80',
  },
  {
    id:5, name:'Snake Plant', slug:'snake-plant', category:'succulents',
    price:549, originalPrice:null, rating:4.9, reviews:445, badge:'Most Popular',
    description:'Bold upright sword-shaped leaves in striking green and yellow. Tolerates neglect beautifully.',
    care:{ light:'Any', water:'Monthly', difficulty:'Very Easy' },
    tags:[], inStock:true, image:'snakeplant',
    imageUrl:'https://images.unsplash.com/photo-1599598425947-5202edd56bdb?w=600&h=600&fit=crop&auto=format&q=80',
  },
  {
    id:3, name:'Pothos Golden', slug:'pothos-golden', category:'trailing',
    price:399, originalPrice:null, rating:4.9, reviews:312, badge:'Easy Care',
    description:'Nearly indestructible and endlessly rewarding. Trails beautifully from shelves.',
    care:{ light:'Low to bright', water:'Every 10 days', difficulty:'Very Easy' },
    tags:[], inStock:true, image:'pothos',
    imageUrl:'https://images.unsplash.com/photo-1591958911259-bee2173bddbf?w=600&h=600&fit=crop&auto=format&q=80',
  },
  {
    id:8, name:'Bird of Paradise', slug:'bird-of-paradise', category:'statement',
    price:2499, originalPrice:2999, rating:4.5, reviews:56, badge:'Luxury',
    description:'Giant paddle-shaped leaves — the crown jewel of indoor plants.',
    care:{ light:'Full to bright', water:'Weekly', difficulty:'Moderate' },
    tags:[], inStock:true, image:'birdofparadise',
    imageUrl:'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=600&fit=crop&auto=format&q=80',
  },
];

const FARM_TO_HOME = [
  {
    title: 'Grown at our farm',
    text: 'Nurtured for 4–8 weeks before reaching you.',
    img: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=500&h=500&fit=crop&auto=format&q=80',
  },
  {
    title: '3-step quality check',
    text: 'Only the healthiest plants make it to you.',
    img: 'https://images.unsplash.com/photo-1545241047-6083a3684587?w=500&h=500&fit=crop&auto=format&q=80',
  },
  {
    title: 'Survival packaging',
    text: 'Moisture-locked, breathable wrap — tested for transit.',
    img: 'https://images.unsplash.com/photo-1600411833114-b04c3a0c4e1c?w=500&h=500&fit=crop&auto=format&q=80',
  },
  {
    title: 'Arrives alive',
    text: '14-day guarantee with free replacement. No questions, just care.',
    img: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&h=500&fit=crop&auto=format&q=80',
  },
];

const SHOP_BY_SPACE = [
  {
    key: 'living-room',
    label: 'Living Room',
    href: '/collections/plants?space=living-room',
    imageUrl: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&h=900&fit=crop&auto=format&q=80',
  },
  {
    key: 'bedroom',
    label: 'Bedroom',
    href: '/collections/plants?space=bedroom',
    imageUrl: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=600&h=900&fit=crop&auto=format&q=80',
  },
  {
    key: 'balcony',
    label: 'Balcony',
    href: '/collections/plants?space=balcony',
    imageUrl: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=900&fit=crop&auto=format&q=80',
  },
  {
    key: 'office',
    label: 'Office',
    href: '/collections/plants?space=office',
    imageUrl: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=900&fit=crop&auto=format&q=80',
  },
];

const GALLERY_IMAGES = [
  { id: 1,  src: 'https://images.unsplash.com/photo-1545241047-6083a3684587?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Monstera in bright living room' },
  { id: 2,  src: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Sunlit plant shelf' },
  { id: 3,  src: 'https://images.unsplash.com/photo-1591958911259-bee2173bddbf?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Pothos trailing from shelf' },
  { id: 4,  src: 'https://images.unsplash.com/photo-1599598425947-5202edd56bdb?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Snake plant in white pot' },
  { id: 5,  src: 'https://images.unsplash.com/photo-1463936575829-25148e1db1b8?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Succulent arrangement' },
  { id: 6,  src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Bird of paradise indoors' },
  { id: 7,  src: 'https://images.unsplash.com/photo-1600411833114-b04c3a0c4e1c?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Fern on wooden table' },
  { id: 8,  src: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Plant corner in bedroom' },
  { id: 9,  src: 'https://images.unsplash.com/photo-1491555103944-7c647fd857e6?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Cacti collection on windowsill' },
  { id: 10, src: 'https://images.unsplash.com/photo-1614594975525-e45190c55d0b?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Monstera close-up leaf' },
  { id: 11, src: 'https://images.unsplash.com/photo-1509423350716-97f9360b4e09?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Hanging plants on balcony' },
  { id: 12, src: 'https://images.unsplash.com/photo-1512428813834-c702c7702b78?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Tropical plant in office' },
  { id: 13, src: 'https://images.unsplash.com/photo-1459156212016-c812468e2115?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Green plants in kitchen' },
  { id: 14, src: 'https://images.unsplash.com/photo-1622547748225-3fc4abd2cca0?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Fiddle leaf fig tree' },
  { id: 15, src: 'https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Terracotta pots on shelf' },
  { id: 16, src: 'https://images.unsplash.com/photo-1567331711402-509c12c41959?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Lush green plant wall' },
  { id: 17, src: 'https://images.unsplash.com/photo-1493238792000-8113da705763?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Plant parent reading by plants' },
  { id: 18, src: 'https://images.unsplash.com/photo-1501004318641-b39e6451bec6?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Peace lily in white room' },
  { id: 19, src: 'https://images.unsplash.com/photo-1536882240095-0379873feb4e?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Tropical leaves pattern' },
  { id: 20, src: 'https://images.unsplash.com/photo-1520412099551-62b6bafeb5bb?w=600&h=600&fit=crop&auto=format&q=80', alt: 'Plant shelfie with grow lights' },
];

const GALLERY_DESKTOP_LIMIT = 16;
const GALLERY_MOBILE_LIMIT  = 9;

// Home categories are fetched dynamically from the server (managed in admin)

const HERO_SLIDES = [
  {
    img: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=1200&h=700&fit=crop&auto=format&q=85',
    title: 'Bring expert care to your green spaces',
    sub: 'From design to maintenance, we help you grow better.',
  },
  {
    img: 'https://images.unsplash.com/photo-1545241047-6083a3684587?w=1200&h=700&fit=crop&auto=format&q=85',
    title: 'Plants that transform every corner',
    sub: 'Handpicked varieties for every light condition and lifestyle.',
  },
  {
    img: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=1200&h=700&fit=crop&auto=format&q=85',
    title: 'Your one-stop indoor garden shop',
    sub: 'Delivered fresh with expert care guides — nationwide.',
  },
];

const testimonials = [
  { name:'Nandini Shah', city:'Jaipur', text:'My Monstera came bigger, bushier than expected — and alive after 3 days in transit.', rating:5, avatar:'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&auto=format&q=80' },
  { name:'Aditya Menon', city:'Bengaluru', text:'The WhatsApp support saved my Fiddle-leaf when the leaves yellowed. Felt like calling a friend.', rating:5, avatar:'https://images.unsplash.com/photo-1500648767791-00d5a4ee9baa?w=120&h=120&fit=crop&auto=format&q=80' },
  { name:'Priya Ramanathan', city:'Chennai', text:'Finally a plant company that actually cares after the sale. Replaced my wilted Peace Lily in 3 days.', rating:5, avatar:'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop&auto=format&q=80' },
  { name:'Gaurav Tripathi', city:'Delhi', text:'Best purchase I\'ve made in months. The plant is healthier and greener than I imagined.', rating:5, avatar:'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&auto=format&q=80' },
  { name:'Meera Tandon', city:'Jaipur', text:'I killed every plant I ever owned. Naturiq matched me with a ZZ Plant and I haven\'t looked back.', rating:5, avatar:'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop&auto=format&q=80' },
];

const IconVerified = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="#1D9BF0">
    <path d="M12 2 9.6 4.4 6.4 4 5.6 7.2 2.4 8 3.6 11l-1.2 3 3.2.8.8 3.2 3.2-.4L12 20l2.4-2.4 3.2.4.8-3.2 3.2-.8L20.4 11l1.2-3-3.2-.8-.8-3.2-3.2.4z"/>
    <path d="M9 12.2l1.8 1.8 4-4.2" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconLeaf = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 8C8 10 5.9 16.17 3.82 19.34a1 1 0 0 0 1.46 1.32C7.5 19 12 17 17 8z"/>
    <path d="M3.82 19.34C4.5 16 6 13 9 11"/>
  </svg>
);

const FAQS = [
  {
    q: 'Is the pot included?',
    a: 'Plants arrive in a nursery grow pot. Decorative planters are sold separately — we have a lovely range if you\'d like one.',
  },
  {
    q: 'Do I need to repot my plant after it arrives?',
    a: 'Not immediately! Most plants are happy in their grow pot for several months. We\'ll include a care card letting you know when it\'s time to upgrade.',
  },
  {
    q: 'Where should I place my plant at home?',
    a: 'Every plant is different! Each listing includes a detailed light and placement guide. As a general rule, most indoor plants love bright indirect light away from harsh afternoon sun.',
  },
  {
    q: 'Do you deliver across India?',
    a: 'Yes — we deliver to 100+ cities across India. Enter your pincode at checkout to confirm availability and estimated delivery time for your area.',
  },
  {
    q: 'How long will delivery take?',
    a: 'Standard delivery takes 3–5 business days. Express 1–2 day delivery is available in select metro cities. All plants are carefully packaged to survive the journey.',
  },
];

// Default order/visibility for homepage sections — overridden by whatever the
// admin has configured in Website Content → Home Page (server: SiteSettings).
const DEFAULT_HOME_SECTIONS = [
  'hero', 'categories', 'featured', 'happyParents',
  'farmHome', 'realStories', 'shopBySpace', 'exploreGallery', 'verticalGarden', 'faq', 'blogSection',
  'collectionSections',
].map((key, order) => ({ key, order, enabled: true, title: '', subtitle: '', description: '', images: [], faqItems: [], ctaText: '', ctaLink: '', bgColor: '', collectionSections: [] }));

// Normalises a product coming back from the /api/collections endpoint
// (Mongo doc shape) into the flat shape ProductCard expects.
function normalisePlantsCollectionProduct(p) {
  return {
    ...p,
    id: p._id || p.id,
    imageUrl: (p.images && p.images[0]?.url) || p.imageUrl || p.image || null,
    price: p.pricing?.salePrice || p.pricing?.basePrice || p.price || 0,
    originalPrice: p.pricing?.basePrice && p.pricing?.salePrice
      ? p.pricing.basePrice
      : p.originalPrice || null,
    rating: p.rating || 0,
    reviews: Array.isArray(p.reviews) ? p.reviews.length : (p.reviews || 0),
  };
}

// Renders a single block from the "Collection Sections" homepage area —
// fetches that block's chosen collection's products and shows them in
// either a grid or a horizontally-scrolling carousel.
function CollectionSectionsBlock({ block }) {
  const [products, setProducts] = useState([]);
  const [collectionMeta, setCollectionMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef(null);
  const slug = block.collectionId;

  useEffect(() => {
    if (!slug) return;
    fetch(`${BASE}/api/collections/${slug}/products?sort=popular`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(data => {
        setCollectionMeta(data.collection || null);
        setProducts((data.products || []).map(normalisePlantsCollectionProduct));
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [slug]);

  if (!slug) return null;

  const heading = block.heading || collectionMeta?.name || block.collectionName || 'Collection';
  const ctaLink = block.ctaLink || `/collections/${slug}`;
  const ctaText = block.ctaText || 'View all →';

  const scroll = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.offsetWidth * 0.75), behavior: 'smooth' });
  };

  if (!loading && products.length === 0) return null;

  return (
    <section
      className="section section--collection-block"
      style={(() => {
        const s = {};
        if (block.bgColor)           s.background   = block.bgColor;
        if (block.paddingTop    != null) s.paddingTop    = block.paddingTop    + 'px';
        if (block.paddingBottom != null) s.paddingBottom = block.paddingBottom + 'px';
        if (block.paddingSide   != null) { s.paddingLeft = block.paddingSide + 'px'; s.paddingRight = block.paddingSide + 'px'; }
        return Object.keys(s).length ? s : undefined;
      })()}
    >
      <div className="section__inner">
        <div className="section__head section__head--row">
          <div>
            {loading ? <div className="skel skel--heading" /> : <h2 className="section__title">{heading}</h2>}
          </div>
          <div className="featured__head-right">
            <Link to={ctaLink} className="section__link">{ctaText}</Link>
            <div className="featured__arrows">
              <button className="featured__arrow" onClick={() => scroll(-1)} aria-label="Previous">
                <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button className="featured__arrow" onClick={() => scroll(1)} aria-label="Next">
                <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
              </button>
            </div>
          </div>
        </div>
        <div className="featured__carousel" ref={carouselRef}>
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="featured__carousel-item">
                  <div className="skel-card">
                    <div className="skel skel--card-img" />
                    <div className="skel-card__body">
                      <div className="skel skel--text-sm" />
                      <div className="skel skel--text-lg" />
                      <div className="skel skel--text-md" />
                      <div className="skel skel--btn" />
                    </div>
                  </div>
                </div>
              ))
            : products.slice(0, 8).map(p => (
                <div className="featured__carousel-item" key={p.id}>
                  <ProductCard product={p} />
                </div>
              ))
          }
        </div>
      </div>
    </section>
  );
}

export default function HomePage({ addToCart }) {
  useSEO({
    title: 'Premium Indoor Plants Delivered Across India',
    description: 'Buy premium indoor plants online in India. Farm-fresh, quality-checked and survival-packed. Free delivery available. Shop Monstera, Pothos, Snake Plant & more.',
    url: '/',
    structuredData: [buildOrganizationSchema(), buildWebSiteSchema()],
  });

  const [plantsCollection, setPlantsCollection] = useState(null);
  const [plantsProducts, setPlantsProducts] = useState(FALLBACK_FEATURED);
  const [plantsLoading, setPlantsLoading] = useState(true);
  const [slide, setSlide] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [homeSections, setHomeSections] = useState(DEFAULT_HOME_SECTIONS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const touchStartX = React.useRef(null);

  // "Featured" section: fetch products from the admin-selected collection.
  // Falls back to /collections/plants if no collection is configured.
  useEffect(() => {
    const featuredSec = homeSections.find(s => s.key === 'featured');
    const collectionSlug = featuredSec?.collectionId || 'plants';
    fetch(`${BASE}/api/collections/${collectionSlug}/products?sort=popular`)
      .then(r => { if (!r.ok) throw new Error(`Server error ${r.status}`); return r.json(); })
      .then(data => {
        setPlantsCollection(data.collection || null);
        const products = (data.products || []).map(normalisePlantsCollectionProduct);
        if (products.length) setPlantsProducts(products);
        setPlantsLoading(false);
      })
      .catch(() => { setPlantsLoading(false); });
  // Re-fetch whenever the admin-selected collection changes
  }, [homeSections.find(s => s.key === 'featured')?.collectionId]);

  useEffect(() => {
    fetch(`${BASE}/api/home-categories`)
      .then(r => r.json())
      .then(data => {
        const cats = Array.isArray(data) ? data : (data.homeCategories || []);
        const active = cats.filter(c => c.active !== false);
        if (active.length) setCategories(active);
        setCategoriesLoading(false);
      })
      .catch(() => { setCategoriesLoading(false); });
  }, []);

  useEffect(() => {
    getBlogPosts({ limit: 4 })
      .then(data => { setBlogPosts(data.posts || []); setBlogLoading(false); })
      .catch(() => { setBlogLoading(false); });
  }, []);

  useEffect(() => {
    getSiteSettings()
      .then(data => {
        const serverSections = Array.isArray(data.homeSections) ? data.homeSections : [];
        if (serverSections.length) {
          const serverKeys = new Set(serverSections.map(s => s.key));
          // For each missing key, find its intended neighbors in DEFAULT_HOME_SECTIONS
          // and assign an order that slots it between them.
          const defaultOrder = DEFAULT_HOME_SECTIONS.map(s => s.key);
          const missingDefaults = DEFAULT_HOME_SECTIONS
            .filter(s => !serverKeys.has(s.key))
            .map(s => {
              const idx = defaultOrder.indexOf(s.key);
              // Find the nearest preceding key that IS in the server list
              let prevOrder = -1;
              for (let i = idx - 1; i >= 0; i--) {
                const prev = serverSections.find(ss => ss.key === defaultOrder[i]);
                if (prev) { prevOrder = prev.order ?? 0; break; }
              }
              // Find the nearest following key that IS in the server list
              let nextOrder = prevOrder + 2;
              for (let i = idx + 1; i < defaultOrder.length; i++) {
                const next = serverSections.find(ss => ss.key === defaultOrder[i]);
                if (next) { nextOrder = next.order ?? prevOrder + 2; break; }
              }
              return { ...s, order: (prevOrder + nextOrder) / 2 };
            });
          setHomeSections(
            [...serverSections, ...missingDefaults].sort((a, b) => a.order - b.order)
          );
        }
        setSettingsLoading(false);
      })
      .catch(() => { setSettingsLoading(false); });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') setLightboxIndex(i => (i + 1) % GALLERY_IMAGES.length);
      if (e.key === 'ArrowLeft')  setLightboxIndex(i => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
      if (e.key === 'Escape')     setLightboxIndex(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex]);

  // ── Admin live-preview bridge ────────────────────────────────────────────
  // When this page is embedded in an iframe by the admin's Home Page Editor
  // (?preview=1), it listens for postMessage'd homeSections so unsaved edits
  // show up instantly without round-tripping through the database. This is a
  // no-op for normal shoppers — nothing here runs unless a parent window is
  // actively sending preview updates.
  const [highlightedSection, setHighlightedSection] = useState(null);

  useEffect(() => {
    const isEmbedded = window.self !== window.top;
    if (!isEmbedded) return;

    const handleMessage = (event) => {
      const msg = event.data;
      if (!msg || msg.source !== 'naturiq-admin-preview') return;
      // Ignore empty payloads — the admin's own settings may not have
      // finished loading yet. An empty array would otherwise blank out
      // every section; safer to keep showing the last good (or default)
      // layout until real data arrives.
      if (msg.type === 'homeSections' && Array.isArray(msg.payload) && msg.payload.length > 0) {
        setHomeSections([...msg.payload].sort((a, b) => a.order - b.order));
      }
      // Highlight a section when selected in the admin editor
      if (msg.type === 'highlightSection') {
        setHighlightedSection(msg.payload || null);
      }
    };
    window.addEventListener('message', handleMessage);

    // Let the parent know the preview frame is ready to receive updates,
    // in case it's already loaded and waiting to push the current draft.
    window.parent.postMessage({ source: 'naturiq-preview-frame', type: 'ready' }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Look up an admin override for a given section key, falling back to
  // enabled=true / blank copy so the page still renders sensibly before the
  // site-settings fetch resolves (or if a key is missing from the response).
  const sectionCfg = (key) => homeSections.find(s => s.key === key) || { enabled: true, title: '', subtitle: '', description: '', images: [], faqItems: [], ctaText: '', ctaLink: '', bgColor: '', collectionSections: [] };
  const isEnabled = (key) => sectionCfg(key).enabled !== false;
  const sectionStyle = (key, extra = {}) => {
    const cfg = sectionCfg(key);
    const style = { ...extra };
    if (cfg.bgColor) style.background = cfg.bgColor;
    if (cfg.paddingTop    != null) style.paddingTop    = cfg.paddingTop    + 'px';
    if (cfg.paddingBottom != null) style.paddingBottom = cfg.paddingBottom + 'px';
    if (cfg.paddingSide   != null) { style.paddingLeft = cfg.paddingSide + 'px'; style.paddingRight = cfg.paddingSide + 'px'; }
    return Object.keys(style).length ? style : undefined;
  };
  const orderedKeys = [...homeSections].sort((a, b) => a.order - b.order).map(s => s.key);

  // In preview mode, wrap each section with a click-to-select overlay
  const isPreviewMode = window.self !== window.top;
  const previewSection = (key, element) => {
    if (!isPreviewMode) return element;
    const isHighlighted = highlightedSection === key;
    return (
      <div
        key={key}
        style={{ position: 'relative', outline: isHighlighted ? '3px solid #2563eb' : '2px solid transparent', outlineOffset: '-2px', transition: 'outline 0.15s', cursor: 'pointer', boxSizing: 'border-box' }}
        onClick={() => window.parent.postMessage({ source: 'naturiq-preview-frame', type: 'sectionClick', payload: key }, '*')}
        title={`Edit this section`}
      >
        {element}
        {isHighlighted && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: '#2563eb', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', letterSpacing: '0.05em', pointerEvents: 'none', zIndex: 9999, fontFamily: 'system-ui, sans-serif' }}>
            ✏️ Editing: {key}
          </div>
        )}
        {!isHighlighted && (
          <div className="preview-hover-hint" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 9998 }}>
            <span style={{ position: 'absolute', top: 6, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 7px', borderRadius: '4px', opacity: 0, transition: 'opacity 0.15s', fontFamily: 'system-ui, sans-serif' }} className="preview-edit-label">
              Click to edit
            </span>
          </div>
        )}
      </div>
    );
  };

  const sections = {

    hero: (() => {
      const cfg = sectionCfg('hero');
      const adminSlides = (cfg.images || []).map(img => ({
        img: img.url,
        title: cfg.title || 'Bring Nature at Your Space.',
        sub: cfg.subtitle || 'Premium indoor plants, delivered to your door.',
      }));
      const slides = adminSlides.length ? adminSlides : HERO_SLIDES;
      const ctaLink = cfg.ctaLink || '/collections/plants';
      const ctaText = cfg.ctaText || 'Shop Now';
      const activeSlide = slide % slides.length;
      return (
        <section className="hero" key="hero">
          <div className="hero__top">
            <h1 className="hero__headline">{cfg.title || 'Bring Nature at Your Space.'}</h1>
          </div>
          <div className="hero__body">
            {/* Left: image slider */}
            <div className="hero__slider">
              {settingsLoading ? (
                <div className="skel skel--hero" />
              ) : slides.map((s, i) => (
                <div key={i} className={`hero__slide${i === activeSlide ? ' hero__slide--active' : ''}`}>
                  <img src={s.img} alt={s.title} className="hero__slide-img" loading={i === 0 ? 'eager' : 'lazy'} />
                  <div className="hero__slide-overlay" />
                  <div className="hero__slide-content">
                    <h2 className="hero__slide-title">{s.title}</h2>
                    <p className="hero__slide-sub">{s.sub}</p>
                    <Link to={ctaLink} className="hero__slide-btn">{ctaText}</Link>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>
      );
    })(),

    categories: (
      <section className="section section--categories" key="categories" style={sectionStyle('categories')}>
        <div className="section__inner">
          <div className="section__head">
            <h2 className="section__title">{sectionCfg('categories').title || 'Find your perfect plant'}</h2>
          </div>
          <div className="categories__row">
            {categoriesLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="category-circle">
                    <div className="category-circle__img-wrap skel skel--circle" />
                    <div className="skel skel--label" />
                  </div>
                ))
              : categories.map(cat => (
                  <Link key={cat._id || cat.key} to={cat.href || `/collections/plants?category=${cat.key}`} className="category-circle">
                    <div className="category-circle__img-wrap">
                      {cat.imageUrl
                        ? <img src={cat.imageUrl} alt={cat.label} className="category-circle__img" loading="lazy" />
                        : <div className="category-circle__img category-circle__img--placeholder" />
                      }
                    </div>
                    <span className="category-circle__label">{cat.label}</span>
                    {cat.description && <span className="category-circle__desc">{cat.description}</span>}
                  </Link>
                ))
            }
          </div>
        </div>
      </section>
    ),

    featured: (() => {
      const featuredScrollRef = useRef(null);
      const scrollFeatured = (dir) => {
        const el = featuredScrollRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * (el.offsetWidth * 0.75), behavior: 'smooth' });
      };
      return (
      <section className="section section--featured" key="featured" style={sectionStyle('featured')}>
        <div className="section__inner">
          <div className="section__head section__head--row">
            <div>
              <p className="section__eyebrow">{sectionCfg('featured').title || 'Handpicked for you'}</p>
              <h2 className="section__title">
                {plantsCollection?.name || sectionCfg('featured').collectionName || 'Plants'}
              </h2>
            </div>
            <div className="featured__head-right">
              <Link
                to={sectionCfg('featured').ctaLink || (sectionCfg('featured').collectionId ? `/collections/${sectionCfg('featured').collectionId}` : '/collections/plants')}
                className="section__link"
              >
                {sectionCfg('featured').ctaText || 'View all plants →'}
              </Link>
              <div className="featured__arrows">
                <button className="featured__arrow" onClick={() => scrollFeatured(-1)} aria-label="Previous">
                  <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <button className="featured__arrow" onClick={() => scrollFeatured(1)} aria-label="Next">
                  <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
                </button>
              </div>
            </div>
          </div>
          <div className="featured__carousel" ref={featuredScrollRef}>
            {plantsLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="featured__carousel-item">
                    <div className="skel-card">
                      <div className="skel skel--card-img" />
                      <div className="skel-card__body">
                        <div className="skel skel--text-sm" />
                        <div className="skel skel--text-lg" />
                        <div className="skel skel--text-md" />
                        <div className="skel skel--btn" />
                      </div>
                    </div>
                  </div>
                ))
              : plantsProducts.slice(0, 8).map(p => (
                  <div className="featured__carousel-item" key={p.id}>
                    <ProductCard product={p} addToCart={addToCart} />
                  </div>
                ))
            }
          </div>
        </div>
      </section>
      );
    })(),

    happyParents: (() => {
      const cfg = sectionCfg('happyParents');
      const adminPhotos = (cfg.images || []).map(img => ({ src: img.url, alt: 'Recently delivered plant' }));
      const photos = adminPhotos.length ? adminPhotos : [
        { src: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&h=700&fit=crop&auto=format&q=80', alt: 'Plant parent with monstera' },
        { src: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=700&fit=crop&auto=format&q=80', alt: 'Plant parent with pothos' },
        { src: 'https://images.unsplash.com/photo-1491555103944-7c647fd857e6?w=600&h=700&fit=crop&auto=format&q=80', alt: 'Plant parent with snake plant' },
        { src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=700&fit=crop&auto=format&q=80', alt: 'Plant parent with bird of paradise' },
        { src: 'https://images.unsplash.com/photo-1600411833114-b04c3a0c4e1c?w=600&h=700&fit=crop&auto=format&q=80', alt: 'Plant parent with fern' },
        { src: 'https://images.unsplash.com/photo-1463936575829-25148e1db1b8?w=600&h=700&fit=crop&auto=format&q=80', alt: 'Plant parent with succulent' },
      ];
      return (
        <section className="happy-parents" key="happyParents" style={sectionStyle('happyParents')}>
          <div className="happy-parents__head">
            <h2 className="happy-parents__title">{cfg.title || 'Recent Delivered Plants'}</h2>
          </div>
          <div className="happy-parents__strip">
            <div className="happy-parents__track">
              {[...photos, ...photos].map((img, i) => (
                <div key={i} className="happy-parents__card">
                  <img src={img.src} alt={img.alt} className="happy-parents__img" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    })(),

    farmHome: (() => {
      const cfg = sectionCfg('farmHome');
      // Admin can configure each step's heading and image via farmSteps array.
      // Falls back to the built-in FARM_TO_HOME defaults.
      const adminSteps = Array.isArray(cfg.farmSteps) && cfg.farmSteps.length > 0 ? cfg.farmSteps : [];
      const steps = FARM_TO_HOME.map((item, i) => ({
        ...item,
        title: adminSteps[i]?.heading    || item.title,
        text:  adminSteps[i]?.subheading || item.text,
        img:   adminSteps[i]?.image      || item.img,
      }));
      return (
        <section className="farm-home" key="farmHome" style={sectionStyle('farmHome')}>
          <div className="farm-home__inner">
            <div className="farm-home__head">
              <h2 className="farm-home__title">{cfg.title || 'From Our Farm to Your Home'}</h2>
              <p className="farm-home__sub">
                {cfg.subtitle || 'Every plant is nursed at our farms, hand-picked, and packed to survive the journey — not just reach your doorstep.'}
              </p>
            </div>
            <div className="farm-home__grid">
              {steps.map((item, i) => (
                <div key={i} className="farm-home__card">
                  <div className="farm-home__img-wrap">
                    <img src={item.img} alt={item.title} className="farm-home__img" loading="lazy" />
                  </div>
                  <div className="farm-home__body">
                    <h3 className="farm-home__card-title">{item.title}</h3>
                    <p className="farm-home__card-text">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    })(),

    shopBySpace: (() => {
      const cfg = sectionCfg('shopBySpace');
      // Merge admin-configured spaceCards with built-in SHOP_BY_SPACE defaults
      const adminCards = Array.isArray(cfg.spaceCards) && cfg.spaceCards.length > 0 ? cfg.spaceCards : [];
      const cards = SHOP_BY_SPACE.map((cat, i) => ({
        ...cat,
        imageUrl:  adminCards[i]?.image   || cat.imageUrl,
        label:     adminCards[i]?.heading  || cat.label,
        ctaText:   adminCards[i]?.buttonText || 'Shop Now',
        href:      adminCards[i]?.href     || cat.href,
      }));
      return (
        <section className="shop-by-space" key="shopBySpace" style={sectionStyle('shopBySpace')}>
          <h2 className="shop-by-space__heading">{cfg.title || 'Transform Your Home.'}</h2>
          <ul className="shop-by-space__grid" role="list">
            {cards.map((cat, idx) => (
              <li key={cat.key || idx} className="shop-by-space__card">
                <Link
                  to={cat.href}
                  className="shop-by-space__card-link"
                  aria-label={`Shop ${cat.label}`}
                >
                  <div
                    className="shop-by-space__card-image"
                    style={{ backgroundImage: cat.imageUrl ? `url(${cat.imageUrl})` : 'none' }}
                  />
                  <div className="shop-by-space__card-overlay" />
                  <div className="shop-by-space__card-content">
                    <span className="shop-by-space__card-title">{cat.label}</span>
                    <span className="shop-by-space__card-cta">{cat.ctaText}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      );
    })(),

    realStories: (() => {
      const cfg = sectionCfg('realStories');
      // Use admin-configured testimonials if available, else fall back to built-in
      const adminTestimonials = Array.isArray(cfg.testimonials) && cfg.testimonials.length > 0
        ? cfg.testimonials.map(t => ({
            name:   t.name   || '',
            city:   t.city   || '',
            text:   t.content || '',
            rating: t.stars  || 5,
            avatar: t.image  || '',
          }))
        : null;
      const stories = adminTestimonials || testimonials;
      return (
        <section className="real-stories" key="realStories" style={sectionStyle('realStories')}>
          <div className="real-stories__inner">
            <h2 className="real-stories__title">{cfg.title || '1,00,000+ plant parents and counting.'}</h2>
            <div className="real-stories__row">
              {stories.map((t, i) => (
                <div key={i} className="story-card">
                  <div className="story-card__stars">
                    {Array.from({ length: t.rating || 5 }).map((_,j) => (
                      <svg key={j} width="16" height="16" viewBox="0 0 24 24" fill="var(--forest-dark)">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    ))}
                  </div>
                  <p className="story-card__text">"{t.text}"</p>
                  <div className="story-card__footer">
                    {t.avatar && <img src={t.avatar} alt={t.name} className="story-card__avatar" loading="lazy" />}
                    <div className="story-card__info">
                      <span className="story-card__name">{t.name} <IconVerified /></span>
                      <span className="story-card__city">{t.city}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    })(),

    exploreGallery: (() => {
      const cfg = sectionCfg('exploreGallery');
      // Use admin-uploaded images when available; fall back to built-in GALLERY_IMAGES
      const adminImgs = (cfg.images || []).map((img, i) => ({
        id: i + 1, src: img.url, alt: `Gallery image ${i + 1}`,
      }));
      const galleryImages = adminImgs.length > 0 ? adminImgs : GALLERY_IMAGES;
      const limit = isMobile ? GALLERY_MOBILE_LIMIT : GALLERY_DESKTOP_LIMIT;
      const visible = galleryImages.slice(0, limit);
      const remaining = galleryImages.length - limit;
      return (
        <section className="explore-gallery" key="exploreGallery" style={sectionStyle('exploreGallery')}>
          <div className="explore-gallery__inner">
            <div className="explore-gallery__head">
              <h2 className="explore-gallery__title">{cfg.title || 'Explore the Gallery'}</h2>
            </div>
            <div className="explore-gallery__grid">
              {visible.map((img, i) => {
                const isLast = i === visible.length - 1 && remaining > 0;
                return (
                  <div
                    key={img.id}
                    className={`explore-gallery__item${isLast ? ' explore-gallery__item--more' : ''}`}
                    onClick={() => setLightboxIndex(isLast ? 0 : i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && setLightboxIndex(isLast ? 0 : i)}
                    aria-label={isLast ? `View all photos` : img.alt}
                  >
                    <img src={img.src} alt={img.alt} className="explore-gallery__img" loading="lazy" />
                    {isLast && (
                      <div className="explore-gallery__more-overlay">
                        <span className="explore-gallery__more-count">+{remaining}</span>
                        <span className="explore-gallery__more-label">more photos</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Lightbox ── */}
          {lightboxIndex !== null && (
            <div
              className="gallery-lightbox"
              onClick={() => setLightboxIndex(null)}
              role="dialog"
              aria-modal="true"
              aria-label="Photo gallery"
              onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
              onTouchEnd={e => {
                if (touchStartX.current === null) return;
                const diff = touchStartX.current - e.changedTouches[0].clientX;
                if (Math.abs(diff) > 50) {
                  if (diff > 0) setLightboxIndex(i => (i + 1) % galleryImages.length);
                  else          setLightboxIndex(i => (i - 1 + galleryImages.length) % galleryImages.length);
                }
                touchStartX.current = null;
              }}
            >
              <button
                className="gallery-lightbox__close"
                onClick={() => setLightboxIndex(null)}
                aria-label="Close gallery"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>

              <button
                className="gallery-lightbox__nav gallery-lightbox__nav--prev"
                onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + galleryImages.length) % galleryImages.length); }}
                aria-label="Previous photo"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>

              <div className="gallery-lightbox__img-wrap" onClick={e => e.stopPropagation()}>
                <img
                  src={galleryImages[lightboxIndex].src}
                  alt={galleryImages[lightboxIndex].alt}
                  className="gallery-lightbox__img"
                />
                <p className="gallery-lightbox__caption">{galleryImages[lightboxIndex].alt}</p>
                <p className="gallery-lightbox__counter">{lightboxIndex + 1} / {galleryImages.length}</p>
              </div>

              <button
                className="gallery-lightbox__nav gallery-lightbox__nav--next"
                onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % galleryImages.length); }}
                aria-label="Next photo"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          )}
        </section>
      );
    })(),

    verticalGarden: (() => {
      const cfg = sectionCfg('verticalGarden');
      const imgSrc = cfg.images?.[0]?.url || 'https://www.calathology.co.uk/storage/2024/06/How-to-Install-a-Vertical-Garden.jpg';
      const ctaLink = cfg.ctaLink || '/collections/plants';
      const ctaText = cfg.ctaText || 'Explore Vertical Gardens';
      return (
      <section className="vertical-garden" key="verticalGarden" style={sectionStyle('verticalGarden')}>
        <div className="vertical-garden__card">
        <div className="vertical-garden__img-side">
          <img
            src={imgSrc}
            alt="Beautiful vertical garden wall with lush green plants"
            className="vertical-garden__img"
            loading="lazy"
          />
        </div>
        <div className="vertical-garden__content-side">
          <p className="vertical-garden__eyebrow">{cfg.title || 'Transform Your Space'}</p>
          <h2 className="vertical-garden__title">{cfg.subtitle || 'Vertical Garden by Naturiq'}</h2>
          <p className="vertical-garden__desc">
            {cfg.description || 'Turn any wall into a living, breathing masterpiece. Our vertical garden solutions bring lush greenery indoors — designed for your space, installed by our experts, and maintained with zero hassle.'}
          </p>
          <ul className="vertical-garden__features">
            <li className="vertical-garden__feature">
              <span className="vertical-garden__feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              Custom-designed for your wall & lighting
            </li>
            <li className="vertical-garden__feature">
              <span className="vertical-garden__feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              Expert installation & ongoing care
            </li>
            <li className="vertical-garden__feature">
              <span className="vertical-garden__feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              Suitable for homes, offices & commercial spaces
            </li>
            <li className="vertical-garden__feature">
              <span className="vertical-garden__feature-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              Air-purifying plant selections included
            </li>
          </ul>
          <Link to={ctaLink} className="vertical-garden__btn">{ctaText}</Link>
        </div>
        </div>
      </section>
      );
    })(),

    faq: (() => {
      const cfg = sectionCfg('faq');
      // Use admin-configured faqItems when available, fall back to built-in FAQS
      const faqList = (Array.isArray(cfg.faqItems) && cfg.faqItems.length > 0)
        ? cfg.faqItems.filter(f => f.q)
        : FAQS;
      return (
        <section className="faq" key="faq" style={sectionStyle('faq')}>
          <div className="faq__inner">
            <h2 className="faq__title">{cfg.title || 'Frequently asked questions'}</h2>
            <div className="faq__list">
              {faqList.map((item, i) => (
                <div key={i} className={`faq__item${openFaq === i ? ' faq__item--open' : ''}`}>
                  <button
                    className="faq__question"
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    aria-expanded={openFaq === i}
                  >
                    <span>{item.q}</span>
                    <svg className="faq__chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div className="faq__answer">{item.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    })(),

    blogSection: (blogLoading || blogPosts.length > 0) ? (
      <section className="blog-section" key="blogSection">
        <div className="blog-section__head">
          <h2 className="blog-section__title">{sectionCfg('blogSection').title || 'From the Garden Journal'}</h2>
          <Link to="/blog" className="blog-section__view-all">View all</Link>
        </div>
        <div className="blog-section__grid">
          {blogLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="blog-card">
                  <div className="skel skel--blog-img" />
                  <div className="skel-card__body">
                    <div className="skel skel--text-lg" />
                    <div className="skel skel--text-md" />
                    <div className="skel skel--text-sm" />
                  </div>
                </div>
              ))
            : blogPosts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="blog-card">
                  <div className="blog-card__img-wrap">
                    <img src={post.cover} alt={post.title} className="blog-card__img" loading="lazy" />
                  </div>
                  <div className="blog-card__body">
                    <h3 className="blog-card__title">{post.title}</h3>
                    <p className="blog-card__excerpt">{post.excerpt}</p>
                    <span className="blog-card__read-more">Read more</span>
                  </div>
                </Link>
              ))
          }
        </div>
      </section>
    ) : null,

    collectionSections: (() => {
      const cfg = sectionCfg('collectionSections');
      const blocks = (cfg.collectionSections || []).filter(b => b.enabled !== false && b.collectionId);
      if (blocks.length === 0) return null;
      return (
        <div className="collection-sections" key="collectionSections">
          {cfg.title && (
            <div className="collection-sections__outer-head">
              <h2 className="section__title">{cfg.title}</h2>
            </div>
          )}
          {blocks.map((block, i) => (
            <CollectionSectionsBlock key={block._id || i} block={block} />
          ))}
        </div>
      );
    })(),

  };

  return (
    <main className="home">
      {orderedKeys.map(key => (isEnabled(key) ? previewSection(key, sections[key]) : null))}
    </main>
  );
}